/*
  Hush v11 — Fill the box: one-click add to cart (+ the pop-up layout).

  Quick add: POSTs to /cart/add.js and stays on the page. On success the
  button flips to the "Added" label and the header cart count updates
  through Dawn's pub/sub (falls back to writing the count directly).
  "Added" is a toggle: tapping it again removes that item from the cart
  (/cart/update.js, quantity 0). Button states are synced from the real
  cart (/cart.js) on load (row) and every time the pop-up opens, so they
  survive closing the pop-up, reloading, or coming back later.

  Pop-up layout ([data-fill-modal]): the pop-up becomes the `cart` of
  every Dawn <product-form> on the page — the same three-method contract
  cart-notification / cart-drawer implement. Dawn keeps doing the add,
  the spinner and the error message; on success it calls renderContents()
  and we open, instead of Dawn redirecting to /cart. X, backdrop and Esc
  close it; the Checkout link goes to /checkout.
*/
(function () {
  'use strict';

  function setState(btn, added) {
    btn.classList.toggle('is-added', added);
    btn.setAttribute('aria-pressed', added ? 'true' : 'false');
    btn.textContent = added ? (btn.dataset.added || 'Added') : (btn.dataset.label || 'Add');
  }

  // Mark every quick-add button by whether its variant is in the cart.
  // Buttons mid-request (or showing an error) are left alone.
  function syncButtons(cart) {
    var inCart = {};
    (cart.items || []).forEach(function (item) { inCart[item.variant_id] = true; });
    document.querySelectorAll('.hv-fill [data-quick-add]').forEach(function (btn) {
      if (btn.classList.contains('is-busy') || btn.classList.contains('is-error')) return;
      setState(btn, !!inCart[parseInt(btn.dataset.variantId, 10)]);
    });
  }

  // Read the cart, then update the header count and the button states.
  // announce = true after we changed the cart ourselves, so Dawn's
  // subscribers hear about it.
  function refreshCart(announce) {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (announce && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'hush-v11-fill-the-box', cartData: cart });
        }
        document.querySelectorAll('#cart-icon-bubble-v11, .cart-count-bubble span[aria-hidden="true"]').forEach(function (el) {
          el.textContent = cart.item_count;
        });
        syncButtons(cart);
        return cart;
      })
      .catch(function () {});
  }

  function postJson(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) {
      if (!r.ok) throw new Error(url + ' failed ' + r.status);
      return r.json();
    });
  }

  function onClick(evt) {
    var btn = evt.target.closest('[data-quick-add]');
    if (!btn || btn.disabled || btn.classList.contains('is-busy')) return;
    evt.preventDefault();

    var id = parseInt(btn.dataset.variantId, 10);
    if (!id) return;

    // "Added" is a toggle: a second tap takes the item back out.
    var removing = btn.classList.contains('is-added');
    var request;
    if (removing) {
      var updates = {};
      updates[id] = 0;
      request = postJson('/cart/update.js', { updates: updates });
    } else {
      request = postJson('/cart/add.js', { items: [{ id: id, quantity: 1 }] });
    }

    btn.classList.add('is-busy');
    btn.classList.remove('is-error');

    request
      .then(function () {
        btn.classList.remove('is-busy');
        setState(btn, !removing);
        return refreshCart(true);
      })
      .catch(function () {
        btn.classList.remove('is-busy');
        btn.classList.add('is-error');
        btn.textContent = btn.dataset.error || 'Try again';
        setTimeout(function () {
          btn.classList.remove('is-error');
          setState(btn, removing); // back to what it was before the tap
        }, 2200);
      });
  }

  // ── Pop-up layout ───────────────────────────────────────────
  var FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function setupModal(root) {
    var dialog = root.querySelector('.hv-fill__dialog');
    var lastFocus = null;
    var fallbackFocus = null; // the Add to cart button (Safari doesn't focus buttons on click)
    var closeTimer = null;
    var locked = false;
    var prevOverflow = '';
    var prevPad = '';

    function lock() {
      if (locked) return;
      locked = true;
      var html = document.documentElement;
      var scrollbar = window.innerWidth - html.clientWidth;
      prevOverflow = html.style.overflow;
      prevPad = document.body.style.paddingRight;
      html.style.overflow = 'hidden';
      if (scrollbar > 0) document.body.style.paddingRight = scrollbar + 'px';
    }
    function unlock() {
      if (!locked) return;
      locked = false;
      document.documentElement.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPad;
    }

    function onKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key !== 'Tab' || !dialog) return;
      var nodes = Array.prototype.filter.call(dialog.querySelectorAll(FOCUSABLE), function (n) {
        return n.getClientRects().length > 0;
      });
      if (!nodes.length) { e.preventDefault(); return; }
      var first = nodes[0];
      var last = nodes[nodes.length - 1];
      var active = document.activeElement;
      if (!dialog.contains(active)) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && (active === first || active === dialog)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    }

    function open() {
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
      lock();
      root.hidden = false;
      void root.offsetWidth; // commit the shown state so the fade/slide runs
      root.classList.add('is-open');
      document.removeEventListener('keydown', onKey);
      document.addEventListener('keydown', onKey);
      if (dialog) { try { dialog.focus({ preventScroll: true }); } catch (e) { dialog.focus(); } }
      // Fresh cart every open: header count + which items already read "Added".
      refreshCart(false);
    }

    function close() {
      if (root.hidden) return;
      root.classList.remove('is-open');
      document.removeEventListener('keydown', onKey);
      unlock();
      if (closeTimer) clearTimeout(closeTimer);
      closeTimer = setTimeout(function () { root.hidden = true; closeTimer = null; }, 220);
      var back = lastFocus || fallbackFocus;
      if (back && document.documentElement.contains(back)) {
        try { back.focus({ preventScroll: true }); } catch (e) { /* no-op */ }
      }
    }

    root.addEventListener('click', function (e) {
      if (e.target.closest('[data-fill-close]')) close();
    });

    // Dawn's product-form "cart" contract.
    root.getSectionsToRender = function () { return [{ id: 'cart-icon-bubble' }]; };
    root.setActiveElement = function (el) { lastFocus = (el && el !== document.body) ? el : null; };
    root.renderContents = function () {
      // A stale pop-up (section removed / re-rendered in the Theme Editor)
      // must never swallow the add: fall back to Dawn's default.
      if (!document.documentElement.contains(root)) {
        window.location = (window.routes && window.routes.cart_url) || '/cart';
        return;
      }
      open();
    };

    function attach() {
      if (!document.documentElement.contains(root)) return;
      document.querySelectorAll('product-form').forEach(function (pf) {
        if (root.contains(pf)) return;
        pf.cart = root;
        var submit = pf.querySelector('[type="submit"]');
        if (submit) {
          submit.setAttribute('aria-haspopup', 'dialog');
          if (!fallbackFocus) fallbackFocus = submit;
        }
      });
    }

    root.__hvFill = { open: open, close: close, attach: attach, unlock: unlock };
    attach();
    // If product-form.js hasn't defined the element yet, its constructor
    // will reset `cart` on upgrade — attach again once it has.
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('product-form').then(attach);
    }
  }

  function init() {
    var needsSync = false;
    document.querySelectorAll('.hv-fill').forEach(function (root) {
      if (root.dataset.hvFillBound) return;
      root.dataset.hvFillBound = '1';
      root.addEventListener('click', onClick);
      if (root.hasAttribute('data-fill-modal')) setupModal(root);
      else if (root.querySelector('[data-quick-add]')) needsSync = true; // row layout is visible now
    });
    if (needsSync) refreshCart(false);
    // Any section re-render can bring new <product-form> elements.
    document.querySelectorAll('[data-fill-modal]').forEach(function (root) {
      if (root.__hvFill) root.__hvFill.attach();
    });
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  // ── Theme Editor ────────────────────────────────────────────
  // Sections re-render in place; the pop-up previews while its section
  // (or one of its blocks) is selected in the sidebar.
  function modalIn(el) {
    if (!el || !el.querySelector) return null;
    return el.matches('[data-fill-modal]') ? el : (el.querySelector('[data-fill-modal]') || el.closest('[data-fill-modal]'));
  }
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', function (e) {
    var m = modalIn(e.target);
    if (m && m.__hvFill) m.__hvFill.unlock();
  });
  document.addEventListener('shopify:section:select', function (e) {
    var m = modalIn(e.target);
    if (m && m.__hvFill) m.__hvFill.open();
  });
  document.addEventListener('shopify:section:deselect', function (e) {
    var m = modalIn(e.target);
    if (m && m.__hvFill) m.__hvFill.close();
  });
  document.addEventListener('shopify:block:select', function (e) {
    var m = modalIn(e.target);
    if (m && m.__hvFill) m.__hvFill.open();
  });
})();
